import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { ThemeProvider, useTheme } from "styled-components";
import { routeList } from "../../App";
import { lang } from "../../assets/languages/en.language";
import DogNameInput from "../../components/form/DogName.input";
import EamilInput from "../../components/form/Email.input";
import PasswordInput from "../../components/form/Password.input";
import UserNameInput from "../../components/form/UserName.input";
import Header from "../../components/Header.component";
import ArrowIcon from "../../components/svgs/icons/Arrow.icon.tsx";
import { storageKey } from "../../configs/config.ts";
import { theme as baseTheme } from "../../configs/theme.ts";
import api from "../../services/api";
import { trackGtagConversion } from "../../services/gtag.ts";
import mixpanel, { setMixpanelUserId } from "../../services/mixpanel.ts";
import { BallButton, NavItem, TertiaryFitContentButton } from "../../styles/Button.style";
import { RootContainer } from "../../styles/Container.style";
import { ErrorMessage, StyledForm } from "../../styles/Form";
import { ColorTokens } from "../../styles/Global.style";
import { Column, Gap } from "../../styles/Utils";

interface FormData {
  email: string;
  dogName: string;
  name: string;
  password: string;
}

export default function SignUpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const { email, dogName } = location.state || {};

  useEffect(() => {
    if (!email || !dogName) navigate(routeList.landing);
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      email: location.state?.email || "",
      dogName: location.state?.dogName || "",
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      const response = await api.signup(data);
      localStorage.setItem(storageKey.accessToken, response.data.accessToken); // Save JWT token
      localStorage.setItem(storageKey.refreshToken, response.data.refreshToken);
      setMixpanelUserId(response.data.accessToken);
      mixpanel.track("Sign Up");
      trackGtagConversion();
      navigate(routeList.addPictures);
    } catch (err) {
      console.log("Error signing up", err);
      alert("Error signing up");
    }
  };

  const themeOveride = {
    ...baseTheme,
    color: {
      ...baseTheme.color,
      inputLabel: `var(${ColorTokens.PrimaryDark})`,
      inputError: `var(${ColorTokens.Tertiary})`,
      inputBackground: `var(${ColorTokens.TertiaryLight})`,
    },
  };

  return (
    <RootContainer $background={theme.color.primaryLight} $topSafe={true}>
      <Column $alignItem="center" $maxHeight>
        <NavItemOvl to={routeList.landing}>
          <BallButton $position="right" $color={theme.color.tertiary}>
            <ArrowIcon color={theme.color.tertiaryLight} />
          </BallButton>
        </NavItemOvl>
        <Header sloganImgSrc="/slogans/slogan-red.png" logoColor={theme.color.primary} />
        <Gap $height={1} />
        <ThemeProvider theme={themeOveride}>
          <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <UserNameInput register={register} name="name" />
            {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
            <Gap $height={1} />
            <DogNameInput register={register} name="dogName" />
            {errors.dogName && <ErrorMessage>{errors.dogName.message}</ErrorMessage>}
            <Gap $height={1} />
            <EamilInput register={register} name="email" />
            <Gap $height={1} />
            <PasswordInput register={register} name="password" />
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
            <Gap $height={2.5} />
            <TertiaryFitContentButton type="submit" disabled={!isValid}>
              <span>{lang.signUpPage.buttons.sign_up}</span>
            </TertiaryFitContentButton>
          </StyledForm>
        </ThemeProvider>
      </Column>
    </RootContainer>
  );
}

const NavItemOvl = styled(NavItem)`
  position: absolute;
  right: ${(props) => props.theme.rootContainerInlinePadding};
`;
